import { Check } from "lucide-react";
import type { FC, ReactNode } from "react";

import { cn } from "@/lib/utils";

type Props = {
  selected: boolean;
  onSelect: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Leading slot: a shortcut key, an avatar, an icon. */
  leading?: ReactNode;
};

/**
 * A selectable option row: a 1px `line-2` ring at rest, the accent soft fill
 * and accent ring with a check once chosen.
 */
export const ChoiceTile: FC<Props> = ({
  selected,
  onSelect,
  title,
  subtitle,
  leading
}) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onSelect}
    className={cn(
      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left ring-1 transition-colors duration-100 outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
      selected
        ? "bg-accent-soft ring-accent-line"
        : "ring-line-2 hover:bg-fill-1"
    )}
  >
    {leading}
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-base font-medium text-fg">{title}</span>
      {subtitle ? (
        <span className="truncate text-sm text-fg-3">{subtitle}</span>
      ) : null}
    </span>
    {selected ? (
      <Check className="size-3.5 shrink-0 text-accent-seed" aria-hidden />
    ) : null}
  </button>
);
